import { h, clear } from './dom.js';
import { ensureTrack, DEFAULT_VELOCITY, DEFAULT_LENGTH } from '../core/pattern.js';

// Channel rack: one row per drum lane, plus an expandable piano-roll block per
// pitched lane. Each pitched lane gets a 12-row × N-step grid scoped to a
// chosen octave, with cells keyed as `${instrumentId}:${noteName}`.

const NOTE_ORDER = ['B', 'A#', 'A', 'G#', 'G', 'F#', 'F', 'E', 'D#', 'D', 'C#', 'C'];
const BLACK_KEYS = new Set(['C#', 'D#', 'F#', 'G#', 'A#']);

// Cells indexed by `${trackKey}|${stepIndex}` for fast playhead repaint.
const cellRefs = new Map();

export function mountChannelRack(ctx, slot) {
    clear(slot);
    cellRefs.clear();

    const header = h('div.lane-header', {}, [h('h2', {}, 'Channel Rack')]);
    const body = h('div.rack-body');
    slot.appendChild(header);
    slot.appendChild(body);

    // Per-lane octave override for pitched lanes (not in pattern data — UI only).
    const octaves = new Map();

    function render() {
        clear(body);
        cellRefs.clear();
        const pattern = ctx.patterns.getActive();
        if (!pattern) return;

        for (const lane of ctx.lanes.all()) {
            if (lane.kind === 'drum') body.appendChild(drumRow(lane, pattern));
            else if (lane.kind === 'pitched') body.appendChild(pitchedBlock(lane, pattern));
        }
    }

    function drumRow(lane, pattern) {
        const trackKey = lane.id;
        const track = ensureTrack(pattern, trackKey);
        const inst = ctx.instruments.get(lane.instrumentId);
        const sampleName = inst?.getSampleName?.() || null;

        const fileInput = h('input', {
            type: 'file', accept: 'audio/*', style: { display: 'none' },
            onChange: async (e) => {
                const file = e.target.files?.[0];
                if (!file || !inst?.loadSample) return;
                try { await inst.loadSample(file); }
                catch (err) { alert(`Couldn't load ${file.name}: ${err.message}`); }
            },
        });

        const side = h('div.row-side' + (sampleName ? '.has-sample' : ''), {
            title: 'Drag a WAV/MP3 here, or click ⤓ to browse',
            ondragover: (e) => { e.preventDefault(); side.classList.add('drag-over'); },
            ondragleave: () => side.classList.remove('drag-over'),
            ondrop: async (e) => {
                e.preventDefault();
                side.classList.remove('drag-over');
                const file = e.dataTransfer?.files?.[0];
                if (!file || !inst?.loadSample) return;
                try { await inst.loadSample(file); }
                catch (err) { alert(`Couldn't load ${file.name}: ${err.message}`); }
            },
        }, [
            h('div.row-side-text', {}, [
                h('span.row-name', {}, lane.name),
                h('span.row-channel', {}, sampleName ? `▸ ${truncate(sampleName, 16)}` : lane.id),
            ]),
            h('div.row-side-actions', {}, [
                h('button.row-btn', {
                    onClick: (e) => { e.stopPropagation(); fileInput.click(); },
                    title: 'Load audio sample',
                }, '⤓'),
                sampleName ? h('button.row-btn.danger', {
                    onClick: (e) => { e.stopPropagation(); inst.clearSample(); },
                    title: 'Remove sample',
                }, '✕') : null,
            ]),
            fileInput,
        ]);

        const cells = h('div.cells');
        for (let i = 0; i < pattern.length; i++) {
            cells.appendChild(stepCell({ trackKey, lane, track, step: i, pattern }));
        }
        return h('div.rack-row.drum-row', {}, [side, cells]);
    }

    function truncate(s, n) { return s.length <= n ? s : s.slice(0, n - 1) + '…'; }

    // Drop zone + load button for a pitched lane that accepts a sample
    // (e.g. the chromatic Sampler instrument).
    function sampleSlot(lane, inst) {
        if (!inst?.loadSample) return null;
        const sampleName = inst.getSampleName?.();
        const fileInput = h('input', {
            type: 'file', accept: 'audio/*', style: { display: 'none' },
            onChange: async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try { await inst.loadSample(file); }
                catch (err) { alert(`Couldn't load ${file.name}: ${err.message}`); }
            },
        });
        const slot = h('div.sample-slot' + (sampleName ? '.has-sample' : ''), {
            title: 'Drag a WAV/MP3 here, or click ⤓ to browse',
            ondragover: (e) => { e.preventDefault(); slot.classList.add('drag-over'); },
            ondragleave: () => slot.classList.remove('drag-over'),
            ondrop: async (e) => {
                e.preventDefault();
                slot.classList.remove('drag-over');
                const file = e.dataTransfer?.files?.[0];
                if (!file) return;
                try { await inst.loadSample(file); }
                catch (err) { alert(`Couldn't load ${file.name}: ${err.message}`); }
            },
        }, [
            h('span.sample-slot-text', {},
                sampleName ? `▸ ${truncate(sampleName, 22)}` : 'no sample · drop here'),
            h('button.row-btn', {
                onClick: (e) => { e.stopPropagation(); fileInput.click(); },
                title: 'Load audio sample',
            }, '⤓'),
            sampleName ? h('button.row-btn.danger', {
                onClick: (e) => { e.stopPropagation(); inst.clearSample(); },
                title: 'Remove sample',
            }, '✕') : null,
            fileInput,
        ]);
        return slot;
    }

    function pitchedBlock(lane, pattern) {
        const inst = ctx.instruments.get(lane.instrumentId);
        const headerControls = h('div.lane-controls', {}, [
            h('label', {}, [
                'Octave',
                octaveSelect(lane),
            ]),
            h('span.hint', {}, 'click to toggle · drag ↕ for velocity · drag → to extend'),
            ...((lane.params || []).map(p => paramControl(p))),
            lane.acceptsSample ? sampleSlot(lane, inst) : null,
        ]);

        const grid = h('div.piano-roll');
        const octave = octaves.get(lane.id) ?? lane.defaultOctave ?? 4;
        for (const noteName of NOTE_ORDER) {
            const note = `${noteName}${octave}`;
            const trackKey = `${lane.id}:${note}`;
            const track = ensureTrack(pattern, trackKey, { note });
            const cells = h('div.cells');
            // Walk the row, skipping cells that are covered by an active note's
            // length so we render multi-step notes as a single wide cell.
            let i = 0;
            while (i < pattern.length) {
                const v = track.steps[i] || 0;
                let span = 1;
                if (v > 0) {
                    const rawLen = (track.lengths && track.lengths[i]) || DEFAULT_LENGTH;
                    span = Math.max(1, Math.min(rawLen, pattern.length - i));
                }
                cells.appendChild(stepCell({
                    trackKey, lane, track, step: i, pattern, note, span,
                    pitched: true,
                }));
                i += span;
            }
            const isBlack = BLACK_KEYS.has(noteName);
            grid.appendChild(h('div.rack-row.pitched-row' + (isBlack ? '.black-key' : ''), {}, [
                h('div.row-side', {}, [h('span.row-name', {}, note)]),
                cells,
            ]));
        }

        return h('div.pitched-block', { dataset: { instrumentId: lane.id } }, [
            h('div.pitched-header', { style: { borderLeftColor: lane.color } }, [
                h('span.pitched-title', {}, lane.name),
                headerControls,
            ]),
            grid,
        ]);
    }

    function paramControl(p) {
        if (p.type === 'select') {
            return h('label', {}, [
                p.label,
                h('select', {
                    onChange: (e) => p.onChange(e.target.value),
                }, p.options.map(o => h('option', { value: o.value, selected: o.value === p.default }, o.label))),
            ]);
        }
        return h('span');
    }

    function octaveSelect(lane) {
        const cur = octaves.get(lane.id) ?? lane.defaultOctave ?? 4;
        const sel = h('select', {
            onChange: (e) => {
                const next = parseInt(e.target.value, 10);
                shiftLaneNotes(lane, cur, next);
                octaves.set(lane.id, next);
                render();
            },
        });
        for (let o = 1; o <= 6; o++) {
            sel.appendChild(h('option', { value: String(o), selected: o === cur }, String(o)));
        }
        return sel;
    }

    // Remap any existing notes from oldOct → newOct in the active pattern, so
    // changing octave shifts the pattern up/down rather than wiping it.
    function shiftLaneNotes(lane, oldOct, newOct) {
        const p = ctx.patterns.getActive();
        const renamed = {};
        for (const [k, v] of Object.entries(p.tracks)) {
            if (!k.startsWith(`${lane.id}:`)) { renamed[k] = v; continue; }
            const noteName = k.slice(lane.id.length + 1).replace(/[0-9]+$/, '');
            renamed[`${lane.id}:${noteName}${newOct}`] = { ...v, note: `${noteName}${newOct}` };
        }
        p.tracks = renamed;
    }

    function stepCell({ trackKey, lane, track, step, pattern, note, span = 1, pitched = false }) {
        const fill = h('div.cell-fill', { style: { background: lane.color } });
        // Probability indicator — small badge in the corner shown only when
        // probability < 100 %.
        const probBadge = h('span.cell-prob');
        const cell = h('div.cell' + (step % 4 === 0 ? '.beat' : ''), {}, [fill, probBadge]);
        if (span > 1) cell.style.gridColumn = `span ${span}`;
        applyVelocity(cell, fill, track.steps[step], lane.color);
        applyProbability(probBadge, track.probabilities?.[step] ?? 1);

        // Right-click cycles probability through 100 → 75 → 50 → 25 → 100.
        cell.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            const cur = track.probabilities?.[step] ?? 1;
            const next = cur > 0.85 ? 0.75 : cur > 0.6 ? 0.5 : cur > 0.35 ? 0.25 : 1;
            if (!track.probabilities) track.probabilities = Array(pattern.length).fill(1);
            track.probabilities[step] = next;
            applyProbability(probBadge, next);
            ctx.bus.emit('history:checkpoint');
        });

        // Mouse interaction modes:
        //   - small movement → click (toggle on/off)
        //   - drag-up/down → velocity (existing)
        //   - drag-right → length, pitched lanes only (new)
        cell.addEventListener('mousedown', (e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const startVel = track.steps[step] || DEFAULT_VELOCITY;
            const startLen = (track.lengths && track.lengths[step]) || DEFAULT_LENGTH;
            // Cell width drives the drag-right → length conversion.
            const cellWidth = cell.getBoundingClientRect().width / span;
            const maxLen = pattern.length - step;
            let dragging = false;
            let mode = null; // 'velocity' | 'length'

            const onMove = (e2) => {
                const dx = e2.clientX - startX;
                const dy = startY - e2.clientY;
                if (!dragging && Math.abs(dx) < 5 && Math.abs(dy) < 4) return;
                dragging = true;
                if (!mode) {
                    // Lock the gesture to whichever axis moved more first.
                    mode = (Math.abs(dx) > Math.abs(dy) && pitched) ? 'length' : 'velocity';
                }
                if (mode === 'velocity') {
                    const v = Math.max(0.1, Math.min(1, startVel + dy / 80));
                    track.steps[step] = v;
                    applyVelocity(cell, fill, v, lane.color);
                } else {
                    const newLen = Math.max(1, Math.min(maxLen, Math.round(startLen + dx / cellWidth)));
                    track.lengths[step] = newLen;
                    cell.style.gridColumn = `span ${newLen}`;
                    if (track.steps[step] === 0) {
                        // Auto-arm if dragging from an empty cell.
                        track.steps[step] = startVel;
                        applyVelocity(cell, fill, track.steps[step], lane.color);
                    }
                }
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                if (dragging) {
                    if (mode === 'length') render(); // re-flow the row to skip covered cells
                    ctx.bus.emit('history:checkpoint');
                    return;
                }
                // Pure click: toggle on/off.
                const wasOn = track.steps[step] > 0;
                track.steps[step] = wasOn ? 0 : DEFAULT_VELOCITY;
                if (track.lengths) track.lengths[step] = DEFAULT_LENGTH;
                applyVelocity(cell, fill, track.steps[step], lane.color);
                cell.style.gridColumn = '';
                if (wasOn && pitched && span > 1) render(); // collapse the wide cell
                if (!wasOn) {
                    const inst = ctx.instruments.get(lane.instrumentId);
                    inst?.play(note ?? null, ctx.Tone.now(), '16n', DEFAULT_VELOCITY);
                }
                ctx.bus.emit('history:checkpoint');
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });
        // Track every step a multi-cell note covers so the playhead can find
        // it as the transport ticks across X+1, X+2, ...
        for (let s = step; s < step + span; s++) cellRefs.set(`${trackKey}|${s}`, cell);
        return cell;
    }

    function applyVelocity(cell, fill, v, color) {
        if (!v) {
            cell.classList.remove('on');
            fill.style.height = '0%';
            return;
        }
        cell.classList.add('on');
        fill.style.height = `${Math.round(v * 100)}%`;
        // Slightly fade lower velocities so light hits read as "ghost notes".
        fill.style.opacity = String(0.45 + v * 0.55);
    }

    function applyProbability(badge, prob) {
        if (prob >= 0.999) {
            badge.textContent = '';
            badge.style.display = 'none';
        } else {
            badge.textContent = `${Math.round(prob * 100)}`;
            badge.style.display = '';
        }
    }

    // ── Playhead ──────────────────────────────────────────────────────────
    let lastPlayedStep = -1;
    function paintPlayhead(step) {
        if (lastPlayedStep === step) return;
        if (lastPlayedStep >= 0) togglePlayheadAt(lastPlayedStep, false);
        togglePlayheadAt(step, true);
        lastPlayedStep = step;
    }
    function togglePlayheadAt(step, on) {
        for (const [k, cell] of cellRefs) {
            const [, sStr] = k.split('|');
            if (parseInt(sStr, 10) === step) cell.classList.toggle('playing', on);
        }
    }
    function clearPlayhead() {
        if (lastPlayedStep >= 0) togglePlayheadAt(lastPlayedStep, false);
        lastPlayedStep = -1;
    }

    render();
    ctx.bus.on('lane:added', render);
    ctx.bus.on('pattern:activeChanged', () => { clearPlayhead(); render(); });
    ctx.bus.on('pattern:listChanged', render);
    ctx.bus.on('pattern:lengthChanged', render);
    ctx.bus.on('pattern:cleared', render);
    ctx.bus.on('project:loaded', render);
    ctx.bus.on('sample:loaded', render);
    ctx.bus.on('sample:cleared', render);
    ctx.bus.on('lane:changed', render);
    ctx.bus.on('rack:playhead', paintPlayhead);
    ctx.bus.on('rack:clearPlayhead', clearPlayhead);
}
